/** @jsx isolateComponent */
import { css } from '@emotion/react';
import { Colors, Icon, Link, Typography } from '@zapier/design-system';
import { isolateComponent } from '@zapier/style-encapsulation';
import type { UniversalNavLinkType } from '../../../constants';
import { emitUserInteractionEvent } from '../../../utils/avroEvents';
import type { AnchorTarget } from '@zapier/design-system';

type Props = {
  /**
   * An array of links
   */
  links?: UniversalNavLinkType[];
  /**
   * Function called when closing menu on a link from within the MegaMenu
   */
  onCloseMenu?: () => void;
};

const Styles = {
  root: css({
    display: 'flex',
    gap: 30,
  }),

  link: css({
    ...Typography.smallPrint1Medium,
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    color: Colors.neutral800,
    '&:hover': {
      color: Colors.zapier,
      textDecoration: 'none',
    },
    '&:focus': {
      outline: `1px solid ${Colors.blue}`,
      outlineOffset: '1px',
      textDecoration: 'none',
    },
  }),
};

/**
 * @private
 * Link content for the Footer of the MegaMenu
 */
export const MegaMenuFooter = (props: Props) => {
  const { links } = props;

  const handleEvent = (text: string, eventType: string) => {
    emitUserInteractionEvent({
      event_action: eventType,
      option_selected: `MegaNavFooter: ${text}`,
    });
    if (eventType === 'click' && props.onCloseMenu) props.onCloseMenu();
  };

  return (
    <ul css={Styles.root}>
      {links?.map((link) => (
        <li key={link.text}>
          <Link
            color={null}
            css={Styles.link}
            href={link.href || undefined}
            onClick={() => handleEvent(link.text, 'click')}
            onMouseEnter={() => handleEvent(link.text, 'hover')}
            target={link.target as AnchorTarget}
          >
            {link.icon && <Icon name={link.icon} size={14} />}
            {link.text}
          </Link>
        </li>
      ))}
    </ul>
  );
};
